import { defineStore } from "pinia"
import { useCookie } from "nuxt/app"
import Dexie from "dexie"
import { ApiService } from "@nuxtDatakodeUtils/utils/api.service"
import type { RuntimeConfig } from "nuxt/schema"
import { useEpciCommuneStore, useNotifyStore, useUserStore } from "./index"
import { toDeepRaw } from "~/utils/raw"
import { useRuntimeConfig } from "#app"

const db = new Dexie("offlineStore")

db.version(1).stores({
    data: "++id, key, value",
})

export async function setter(key, value) {
    const val = toDeepRaw(value)
    const r = await db.data.where("key").equals(key).first()
    if (r) {
        await db.data.delete(r.id)
    }
    await db.data.add({ key, value: val })
}

export async function unsetter(key) {
    await db.data.where("key").equals(key).delete()
}

export async function getter(key) {
    const d = await db.data.where("key").equals(key).first()
    if (d) {
        return d.value
    }
    return null
}

export const baseUseOnlineStore = defineStore("online", {

    state: () => {
        return {
            online: true,
            onlineLoading: false,
            offlineReady: false,
            onlineReady: true,
            loaded: 0,
        }
    },
    actions: {
        async prepareOffline(requestName, translationName) {
            if (typeof window === "undefined" || !window || !window.indexedDB || !useUserStore().currentUser?.id || useUserStore().currentUser?.role !== "admin") {
                this.offlineReady = false
                return
            }
            const commune = useEpciCommuneStore().currentEpciCommune
            if (!commune?.id) {
                return
            }
            this.loaded = 0
            this.onlineLoading = true

            const access_token = useCookie<string>("access_token")
            const config: RuntimeConfig = useRuntimeConfig()
            const apiService = new ApiService($fetch, config, access_token, useNotifyStore(), {}, useUserStore())
            // We are online, load full data

            let data = []
            let page = 1
            let res = await apiService.getListData({
                url: requestName,
                translation: translationName,
                pagination: { number: page, size: 100 },
                query: {
                    limit_to_accessible_for: commune.id,
                },
            })
            const last_page = res.meta.last_page

            const total_requests = last_page + 15
            this.loaded = page / total_requests * 100
            await setter(`${requestName}-meta`, res.meta)
            data = [...res.data]
            page += 1
            while (res.meta.next_page_url) {
                res = await apiService.getListData({
                    url: requestName,
                    translation: translationName,
                    pagination: { number: page, size: 100 },
                    query: {
                        limit_to_accessible_for: commune.id,
                    },
                })
                this.loaded = page / total_requests * 100
                page += 1
                data = [...data, ...res.data]
            }

            await setter(`${requestName}-data`, data)

            const create = await apiService.getData({
                url: `${requestName}/create`,
            })
            page += 1
            this.loaded = page / total_requests * 100
            await setter(`${requestName}-create-data`, create.data)
            await setter(`${requestName}-create-meta`, create.meta)

            // get all autocompletes

            const items = {
                "epci-communes": "",
                "series": "",
                "sub-series": "",
                "boxes": `&filter[where_in_epci_commune]=${commune.id}`,
                "communication-delays": "",
                "administrative-retention-periods": "",
                "racks": `&filter[where_in_epci_commune]=${commune.id}`,
                "cabinets": `&filter[where_in_epci_commune]=${commune.id}`,
                "cabinet-clusters": `&filter[where_in_epci_commune]=${commune.id}`,
                "archive-rooms": `&filter[where_in_epci_commune]=${commune.id}`,
                "indices": "",
            }
            if (useUserStore().currentUser && useUserStore().currentUser.role === "admin") {
                items.missions = ""
            }
            const query = "page[size]=10000&page[number]=1"

            const p = Promise.all(Object.keys(items).map((el) => {
                const req = apiService.setRequest({
                    url: `${el}?${query}${items[el]}`,
                    method: "GET",
                }, true)
                req.then(async (r) => {
                    // localStorage.setItem(`${el}-list`, JSON.stringify(r.data))
                    await setter(`${el}-list`, r.data)
                    page += 1
                    this.loaded = page / total_requests * 100
                })
                return req
            }))
            await p
            this.offlineReady = true
            this.onlineLoading = false
        },
        async setOnline(val: boolean) {
            this.onlineReady = false
            if (val === true && this.online === false && window && window.indexedDB) {
                this.online = val
                const access_token = useCookie<string>("access_token")
                const config: RuntimeConfig = useRuntimeConfig()
                const apiService = new ApiService($fetch, config, access_token, useNotifyStore(), {}, useUserStore())
                // We are back online, start by deleting items on the server
                const toDelete = await getter(`archive-records-to-delete`) || []
                const failedDeletions = []
                const failedUpdates = []
                const failedCreations = []
                let errors = false
                if (toDelete) {
                    // only delete those that have an ID
                    for (const i in toDelete.filter(d => d.id)) {
                        const d = toDelete[i]
                        try {
                            await apiService.setRequest({
                                url: `archive-records/${d.id}`,
                                method: "DELETE",
                                body: {},
                            }, true)
                        }
                        catch {
                            errors = true
                            failedDeletions.push(d)
                        }
                    }
                }

                // update modified elements
                const toPut = await getter(`archive-records-to-put`) || []

                if (toPut) {
                    // only update those that have an ID
                    for (const i in toPut.filter(d => d.id)) {
                        const d = toPut[i]
                        try {
                            await apiService.setRequest({
                                url: `archive-records/${d.id}`,
                                method: "PUT",
                                body: { ...d },
                            }, true)
                        }
                        catch {
                            errors = true
                            failedUpdates.push(d)
                        }
                    }
                }

                // TODO upload new elements (those that have no ID)

                const toPost = await getter(`archive-records-to-post`) || []
                if (toPost) {
                    for (const i in toPost) {
                        const d = toPost[i]
                        try {
                            await apiService.setRequest({
                                url: `archive-records`,
                                method: "POST",
                                body: { ...d },
                            }, true)
                        }
                        catch {
                            errors = true
                            failedCreations.push(d)
                        }
                    }
                }

                if (toPost.length > 0 || toPut.length > 0 || toDelete.length > 0) {
                    if (errors) {
                        console.error("could not sync all", toPost)
                        await setter(`archive-records-to-delete`, failedDeletions)
                        await setter(`archive-records-to-put`, failedUpdates)
                        await setter(`archive-records-to-post`, failedCreations)
                        useNotifyStore().showMessage({
                            title: "Erreurs de synchronisation",
                            text: `Toutes les données n'ont pas pu être synchronisées. Rechargez la page et vérifiez vos données`,
                            color: "warning",
                            icon: "mdi-alert-circle",
                            timer: 8000,
                        })
                    }
                    else {
                        useNotifyStore().showMessage({
                            title: "Données synchronisées",
                            text: `${toPost.length} créations, ${toPut.length} mises à jour, ${toDelete.length} suppressions`,
                            color: "success",
                            icon: "mdi-check-circle",
                            timer: 8000,
                        })
                        await unsetter(`archive-records-to-post`)
                        await unsetter(`archive-records-to-delete`)
                        await unsetter(`archive-records-to-put`)
                    }
                }
            }
            // Set this at the bottom to be able to check if it changed
            this.online = val
            this.onlineReady = true
        },
    },
})
